import { Component } from '@theme/component';

/** shippingCategory -> storefront display label. */
const SHIPPING_CATEGORY_LABELS = {
  AustraliaPostInternationalCommercial: 'Commercial Express',
  InternationalExpress: 'DHL Express',
  InternationalPostSeko: 'SEKO Tracked Standard',
};

/** Currency code -> symbol for display. */
const CURRENCY_SYMBOLS = {
  GBP: '£',
  CAD: '$',
  NZD: '$',
  USD: '$',
  AUD: '$',
  EUR: '€',
};

const LOCATION_KEY = 'sabo_delivery_location';

/**
 * Shipping Calculator Component
 * Provides autocomplete country selection and shipping calculation.
 * For Australia, optionally merges Same Day rows when a postcode is entered.
 */
export default class ShippingCalculatorComponent extends Component {
  /** @type {Array<{id: string, name: string}>} */
  #countries = [];

  /** @type {Array<{id: string, name: string}>} */
  #filteredCountries = [];

  /** @type {number} */
  #selectedIndex = -1;

  /** @type {string | null} */
  #selectedCountryId = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {AbortController | null} */
  #sameDayAbort = null;

  /** Last successful country rates payload (reused when only suburb changes). */
  #lastShippingData = null;

  /** @type {string[]} */
  #suburbOptions = [];

  /** @type {string} */
  #lastPostcodeDigits = '';

  /** @type {ReturnType<typeof setTimeout> | null} */
  #postcodeTimer = null;

  /** @type {(event: Event) => void} */
  #onDelegatedChange = this.#handleDelegatedChange.bind(this);

  /** @type {(event: Event) => void} */
  #onDelegatedInput = this.#handleDelegatedInput.bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.#initializeCountries();
    this.#setupEventListeners();
    this.#prefillStoredLocation();
    this.#autoFetchInitialShippingOptions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#sameDayAbort?.abort();
    if (this.#postcodeTimer) clearTimeout(this.#postcodeTimer);
    this.removeEventListener('change', this.#onDelegatedChange);
    this.removeEventListener('input', this.#onDelegatedInput);
  }

  #initializeCountries() {
    try {
      const countryDataElement = this.refs.countryData;
      if (countryDataElement) {
        this.#countries = JSON.parse(countryDataElement.textContent);
        this.#filteredCountries = [...this.#countries];
      }
    } catch (error) {
      console.error('Failed to parse country data:', error);
    }
  }

  #setupEventListeners() {
    this.refs.countryInput?.addEventListener('input', this.#handleInput.bind(this));
    this.refs.countryInput?.addEventListener('focus', this.#handleFocus.bind(this));
    this.refs.countryInput?.addEventListener('blur', this.#handleBlur.bind(this));
    this.refs.countryInput?.addEventListener('keydown', this.#handleKeydown.bind(this));
    this.refs.form?.addEventListener('submit', this.#handleSubmit.bind(this));
    // Delegation so suburb/postcode keep working even if refs refresh.
    this.addEventListener('change', this.#onDelegatedChange);
    this.addEventListener('input', this.#onDelegatedInput);

    document.addEventListener('click', this.#handleDocumentClick.bind(this));
  }

  /** @param {Event} event */
  #handleDelegatedChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (target !== this.#suburbEl()) return;
    this.#onSuburbSelected();
  }

  /** @param {Event} event */
  #handleDelegatedInput(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target !== this.#postcodeEl()) return;

    const digits = this.#padPostcode(target.value);
    if (digits !== this.#lastPostcodeDigits) {
      this.#lastPostcodeDigits = digits;
      this.#hideSuburbField();
      this.#setAuHint('Enter your postcode to check Same Day availability.');
    }

    if (this.#postcodeTimer) clearTimeout(this.#postcodeTimer);
    if (digits.length !== 4) return;

    // Auto-update results once postcode is complete — no Calculate click needed.
    this.#postcodeTimer = setTimeout(() => {
      const countryId = this.refs.countryIdInput?.value;
      if (!countryId || !this.#isAustralia(countryId)) return;
      if (this.#getPostcode() !== digits) return;
      this.#fetchShippingOptions(countryId);
    }, 280);
  }

  #onSuburbSelected() {
    const countryId = this.refs.countryIdInput?.value;
    if (!countryId || !this.#isAustralia(countryId)) return;
    const postcode = this.#getPostcode();
    if (postcode.length !== 4) {
      this.#setAuHint('Enter a 4-digit postcode first.');
      return;
    }

    const suburb = this.#getSuburb();
    if (!suburb) {
      this.#setAuHint('Select your suburb to check Same Day.');
      if (this.#lastShippingData) {
        this.#showResults(this.#lastShippingData, {
          rows: [
            {
              name: 'Same Day Delivery',
              deliveryHtml: 'Select suburb to check',
              priceHtml: `From ${this.#sameDayPrice()}`,
              pending: true,
            },
          ],
          note: `Postcode ${postcode} needs a suburb. Select it above to check Same Day.`,
        });
      }
      return;
    }

    // Suburb chosen: refresh rates + Same Day without requiring Calculate.
    this.#fetchShippingOptions(countryId);
  }

  /** @returns {HTMLInputElement | null} */
  #postcodeEl() {
    return this.refs.postcodeInput || this.querySelector('[ref="postcodeInput"]');
  }

  /** @returns {HTMLSelectElement | null} */
  #suburbEl() {
    return this.refs.suburbSelect || this.querySelector('[ref="suburbSelect"]');
  }

  #getPostcode() {
    return this.#padPostcode(this.#postcodeEl()?.value);
  }

  #getSuburb() {
    return String(this.#suburbEl()?.value || '').trim();
  }

  #setAuHint(text) {
    const hint = this.querySelector('.shipping-calculator__au-hint');
    if (hint) hint.textContent = text;
  }

  #australiaCountryId() {
    return String(this.dataset.australiaCountryId || '13');
  }

  #isAustralia(countryId) {
    return String(countryId || '') === this.#australiaCountryId();
  }

  #availabilityUrl() {
    return (this.dataset.sameDayAvailabilityUrl || '').trim();
  }

  #sameDayPrice() {
    return (this.dataset.sameDayPrice || '$14.95').trim();
  }

  #padPostcode(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 4);
    return digits;
  }

  #readStoredLocation() {
    try {
      const raw = localStorage.getItem(LOCATION_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.postcode) return null;
      return {
        postcode: this.#padPostcode(data.postcode),
        suburb: String(data.suburb || '').trim(),
      };
    } catch (_e) {
      return null;
    }
  }

  #writeStoredLocation(postcode, suburb, state) {
    try {
      localStorage.setItem(
        LOCATION_KEY,
        JSON.stringify({
          postcode: this.#padPostcode(postcode),
          suburb: String(suburb || '').trim(),
          state: String(state || '').trim(),
          fetchedAt: Date.now(),
        })
      );
      document.dispatchEvent(
        new CustomEvent('sabo:delivery-location-changed', {
          detail: {
            postcode: this.#padPostcode(postcode),
            suburb: String(suburb || '').trim(),
            source: 'shipping-calculator',
          },
        })
      );
    } catch (_e) {
      /* ignore quota / private mode */
    }
  }

  #prefillStoredLocation() {
    const stored = this.#readStoredLocation();
    const postcodeInput = this.#postcodeEl();
    if (!stored || !postcodeInput) return;
    postcodeInput.value = stored.postcode;
    this.#lastPostcodeDigits = stored.postcode;
    const suburbSelect = this.#suburbEl();
    if (stored.suburb && suburbSelect) {
      // Suburb options load after availability; stash for later.
      suburbSelect.dataset.pendingSuburb = stored.suburb;
    }
  }

  #auFieldsEl() {
    return this.refs.auFields || this.querySelector('[ref="auFields"]') || this.querySelector('.shipping-calculator__au-fields');
  }

  #syncAuFields(countryId) {
    const isAu = this.#isAustralia(countryId);
    const showChecker = isAu && !!this.#availabilityUrl();
    const auFields = this.#auFieldsEl();
    if (auFields) {
      auFields.hidden = !showChecker;
      auFields.setAttribute('aria-hidden', showChecker ? 'false' : 'true');
    }
    if (!showChecker) this.#hideSuburbField();
    // Hide every Same Day surface when a non-AU country is selected.
    this.#syncSameDayDetailsSection(isAu);
    if (!isAu) this.#stripSameDayFromResults();
  }

  /** @param {boolean} show */
  #syncSameDayDetailsSection(show) {
    const candidates = [
      document.querySelector('.shopify-section.section-same-day-delivery'),
      document.querySelector('.section-same-day-delivery'),
      document.getElementById('same-day')?.closest('.shopify-section'),
      document.getElementById('same-day'),
      document.querySelector('.sabo-same-day-hero-section'),
      document.querySelector('.sabo-sd-hero')?.closest('.shopify-section'),
    ].filter(Boolean);

    for (const el of new Set(candidates)) {
      el.hidden = !show;
    }
  }

  /** Remove Same Day rows/notes from the current results list immediately. */
  #stripSameDayFromResults() {
    const root = this.refs.resultsContent;
    if (!root) return;
    root.querySelectorAll('.shipping-calculator__rate--same-day, .shipping-calculator__same-day-note').forEach((el) => {
      el.remove();
    });
  }

  #hideSuburbField() {
    const select = this.#suburbEl();
    const field = select?.closest('.shipping-calculator__suburb-field');
    if (field) field.hidden = true;
    this.#suburbOptions = [];
    if (select) {
      select.innerHTML = '<option value="">Select suburb</option>';
      select.value = '';
      delete select.dataset.pendingSuburb;
    }
  }

  /**
   * @param {unknown} suburbs
   * @returns {string[]}
   */
  #normalizeSuburbList(suburbs) {
    const out = [];
    const seen = new Set();
    for (const item of Array.isArray(suburbs) ? suburbs : []) {
      const name =
        typeof item === 'string'
          ? item.trim()
          : String((item && (item.name || item.suburb || item.value)) || '').trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
    return out;
  }

  #titleCaseSuburb(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * @param {unknown} suburbs
   * @param {string} [preferred]
   * @param {{ autoFetch?: boolean }} [options] - re-check when a pending suburb is restored
   */
  #showSuburbOptions(suburbs, preferred, options = {}) {
    const select = this.#suburbEl();
    const field = select?.closest('.shipping-calculator__suburb-field');
    if (!select || !field) return;

    const list = this.#normalizeSuburbList(suburbs);
    if (list.length) this.#suburbOptions = list;

    const pending = String(preferred || select.dataset.pendingSuburb || select.value || '').trim();
    select.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select suburb';
    select.appendChild(placeholder);

    let matched = '';
    list.forEach((name) => {
      const opt = document.createElement('option');
      opt.value = name;
      opt.textContent = this.#titleCaseSuburb(name);
      select.appendChild(opt);
      if (pending && name.toLowerCase() === pending.toLowerCase()) matched = name;
    });

    select.value = matched || '';
    if (matched) delete select.dataset.pendingSuburb;

    field.hidden = list.length === 0;
    if (!field.hidden && !matched) {
      this.#setAuHint('Select your suburb to check Same Day.');
    }

    // Pending suburb restored from storage: re-check without asking for postcode again.
    if (options.autoFetch && this.#getSuburb()) {
      const countryId = this.refs.countryIdInput?.value;
      if (countryId) queueMicrotask(() => this.#fetchShippingOptions(countryId));
    }
  }

  /** Keep suburb dropdown filled after a successful check so it never looks empty. */
  #persistSuburbPicker(suburb) {
    const select = this.#suburbEl();
    const field = select?.closest('.shipping-calculator__suburb-field');
    if (!select || !field) return;
    if (!this.#suburbOptions.length) return;
    this.#showSuburbOptions(this.#suburbOptions, suburb, { autoFetch: false });
    field.hidden = false;
    this.#setAuHint('Change suburb and results will update.');
  }

  #handleInput(event) {
    const query = event.target.value.toLowerCase().trim();

    if (!query) {
      this.#filteredCountries = [...this.#countries];
    } else {
      this.#filteredCountries = this.#countries.filter((country) =>
        country.name.toLowerCase().includes(query)
      );
    }

    this.#selectedIndex = -1;
    this.#updateDropdown();
    this.#showDropdown();
    this.#updateSubmitButton();
  }

  #handleFocus() {
    this.#showDropdown();
  }

  #handleBlur() {
    setTimeout(() => {
      this.#hideDropdown();
    }, 150);
  }

  #handleKeydown(event) {
    if (!this.#isDropdownVisible()) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#selectedIndex = Math.min(this.#selectedIndex + 1, this.#filteredCountries.length - 1);
        this.#updateSelection();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.#selectedIndex = Math.max(this.#selectedIndex - 1, 0);
        this.#updateSelection();
        break;

      case 'Enter':
        event.preventDefault();
        if (this.#selectedIndex >= 0) {
          this.#selectCountry(this.#filteredCountries[this.#selectedIndex]);
        }
        break;

      case 'Escape':
        this.#hideDropdown();
        break;
    }
  }

  async #handleSubmit(event) {
    event.preventDefault();

    const countryId = this.refs.countryIdInput?.value;
    if (!countryId) {
      this.#showError('Please select a country');
      return;
    }

    await this.#fetchShippingOptions(countryId);
  }

  #handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.#hideDropdown();
    }
  }

  #updateDropdown() {
    const optionsList = this.refs.optionsList;
    if (!optionsList) return;

    optionsList.innerHTML = '';

    if (this.#filteredCountries.length === 0) {
      optionsList.innerHTML = '<li class="shipping-calculator__option">No results found</li>';
      return;
    }

    this.#filteredCountries.forEach((country) => {
      const li = document.createElement('li');
      li.className = 'shipping-calculator__option';
      li.dataset.countryId = country.id;
      li.setAttribute('role', 'option');
      li.setAttribute('tabindex', '-1');

      if (country.id === this.#selectedCountryId) {
        li.setAttribute('aria-current', 'true');
      }

      const checkmark = document.createElement('span');
      checkmark.className = 'svg-wrapper icon-checkmark';
      checkmark.innerHTML = this.#getCheckmarkSVG();

      const textSpan = document.createElement('span');
      textSpan.className = 'shipping-calculator__option-text';
      textSpan.textContent = country.name;

      li.appendChild(checkmark);
      li.appendChild(textSpan);

      li.addEventListener('click', () => this.#selectCountry(country));

      optionsList.appendChild(li);
    });
  }

  #getCheckmarkSVG() {
    return `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-checkmark">
        <path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
    `;
  }

  #updateSelection() {
    const options = this.refs.optionsList?.querySelectorAll('.shipping-calculator__option');
    if (!options) return;

    options.forEach((option, index) => {
      option.classList.toggle('shipping-calculator__option--selected', index === this.#selectedIndex);
    });

    if (this.#selectedIndex >= 0 && options[this.#selectedIndex]) {
      options[this.#selectedIndex].scrollIntoView({ block: 'nearest' });
    }
  }

  #selectCountry(country) {
    this.refs.countryInput.value = country.name;
    this.refs.countryIdInput.value = country.id;
    this.#selectedCountryId = country.id;

    this.#updateDropdown();
    this.#hideDropdown();
    this.#updateSubmitButton();
    this.#clearError();
    this.#syncAuFields(country.id);
    // Update results as soon as a country is chosen — no Calculate click needed.
    this.#fetchShippingOptions(country.id);
  }

  #showDropdown() {
    if (this.refs.dropdown) {
      this.#updateDropdown();
      this.refs.dropdown.hidden = false;
      this.refs.countryInput?.setAttribute('aria-expanded', 'true');
    }
  }

  #hideDropdown() {
    if (this.refs.dropdown) {
      this.refs.dropdown.hidden = true;
      this.refs.countryInput?.setAttribute('aria-expanded', 'false');
    }
  }

  #isDropdownVisible() {
    return this.refs.dropdown && !this.refs.dropdown.hidden;
  }

  #updateSubmitButton() {
    const hasSelection = this.refs.countryIdInput?.value && this.refs.countryIdInput.value !== '';
    if (this.refs.submitButton) {
      this.refs.submitButton.disabled = !hasSelection;
    }
  }

  async #autoFetchInitialShippingOptions() {
    setTimeout(async () => {
      try {
        const initialCountryId = this.dataset.initialCountryId || '13';
        const match = this.#countries.find((c) => String(c.id) === String(initialCountryId));
        if (match && this.refs.countryInput && this.refs.countryIdInput) {
          this.refs.countryInput.value = match.name;
          this.refs.countryIdInput.value = match.id;
          this.#selectedCountryId = match.id;
        }
        this.#syncAuFields(initialCountryId);
        await this.#fetchShippingOptions(initialCountryId);
        this.#updateSubmitButton();
      } catch (error) {
        console.error('Auto-fetch of initial shipping options failed:', error);
      }
    }, 100);
  }

  #getApiCurrency() {
    return this.dataset.cartCurrency || 'AUD';
  }

  async #fetchShippingOptions(countryId) {
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    const apiUrl = this.refs.countryIdInput?.dataset.apiUrl?.replace(/\{\{\s*countryId\s*\}\}/gi, countryId);
    if (!apiUrl) {
      this.#showError('API URL not configured');
      return;
    }

    this.#setLoadingState(true);
    this.#clearError();
    this.#syncAuFields(countryId);

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      let sameDay = { rows: [], note: '' };

      if (this.#isAustralia(countryId) && this.#availabilityUrl()) {
        sameDay = await this.#fetchSameDayAvailability(controller.signal);
      }

      if (controller.signal.aborted) return;
      this.#lastShippingData = data;
      this.#showResults(data, sameDay);
    } catch (error) {
      if (error.name === 'AbortError') {
        return; // Request was cancelled
      }

      console.error('Shipping calculation failed:', error);
      this.#showError('Failed to calculate shipping. Please try again.');
    } finally {
      if (this.#abortController === controller) {
        this.#abortController = null;
        this.#setLoadingState(false);
      }
    }
  }

  /** Re-check Same Day only (keeps current postcode + standard rates). */
  async #refreshSameDayOnly() {
    const countryId = this.refs.countryIdInput?.value;
    if (!countryId || !this.#isAustralia(countryId) || !this.#availabilityUrl()) return;

    if (!this.#lastShippingData) {
      await this.#fetchShippingOptions(countryId);
      return;
    }

    this.#sameDayAbort?.abort();
    const controller = new AbortController();
    this.#sameDayAbort = controller;
    this.#setLoadingState(true);
    this.#clearError();

    try {
      const sameDay = await this.#fetchSameDayAvailability(controller.signal);
      if (controller.signal.aborted) return;
      this.#showResults(this.#lastShippingData, sameDay);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Same Day refresh failed:', error);
    } finally {
      if (this.#sameDayAbort === controller) {
        this.#sameDayAbort = null;
        this.#setLoadingState(false);
      }
    }
  }

  /**
   * @param {AbortSignal} [signal]
   * @returns {Promise<{ rows: Array<{name: string, deliveryHtml: string, priceHtml: string}>, note: string }>}
   */
  async #fetchSameDayAvailability(signal) {
    const empty = { rows: [], note: '' };
    const postcode = this.#getPostcode();
    if (!postcode) {
      return {
        rows: [
          {
            name: 'Same Day Delivery',
            deliveryHtml: 'Enter postcode to check',
            priceHtml: `From ${this.#sameDayPrice()}`,
            pending: true,
          },
        ],
        note: '',
      };
    }
    if (postcode.length !== 4) {
      return {
        rows: [
          {
            name: 'Same Day Delivery',
            deliveryHtml: 'Enter a 4-digit postcode',
            priceHtml: `From ${this.#sameDayPrice()}`,
            pending: true,
          },
        ],
        note: '',
      };
    }

    const suburb = this.#getSuburb();
    const base = this.#availabilityUrl();
    let url;
    try {
      url = new URL(base, window.location.origin);
    } catch (_e) {
      return empty;
    }
    url.searchParams.set('postcode', postcode);
    if (suburb) url.searchParams.set('suburb', suburb);
    else url.searchParams.delete('suburb');

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal,
      });
      if (!response.ok) throw new Error('bad status');
      const data = await response.json();
      return this.#sameDayFromAvailability(data, postcode, suburb);
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return {
        rows: [
          {
            name: 'Same Day Delivery',
            deliveryHtml: 'Temporarily unavailable',
            priceHtml: `From ${this.#sameDayPrice()}`,
            pending: true,
          },
        ],
        note: 'We could not check Same Day just now. Standard options are still shown below.',
      };
    }
  }

  /**
   * @param {any} data
   * @param {string} postcode
   * @param {string} suburb
   */
  #sameDayFromAvailability(data, postcode, suburb) {
    let state = String((data && data.state) || '').toLowerCase();
    if (!state && data && data.enabled === false) state = 'not_serviced';
    if (!state && data && Array.isArray(data.windows)) {
      state = data.windows.length ? 'ok' : 'empty';
    }

    if (state === 'suburb_required') {
      // Restore pending suburb and auto-refetch — postcode stays as entered.
      this.#showSuburbOptions(data.suburbs || [], suburb, { autoFetch: !suburb });
      const selected = this.#getSuburb();
      this.#writeStoredLocation(postcode, selected, 'suburb_required');
      if (selected && selected.toLowerCase() === String(suburb || '').toLowerCase() && suburb) {
        // Suburb was sent but API still wants one — keep picker, avoid empty state.
        this.#persistSuburbPicker(selected);
      }
      return {
        rows: [
          {
            name: 'Same Day Delivery',
            deliveryHtml: selected ? 'Checking suburb…' : 'Select suburb to check',
            priceHtml: `From ${this.#sameDayPrice()}`,
            pending: true,
          },
        ],
        note: selected
          ? ''
          : `Postcode ${postcode} needs a suburb. Select it above to check Same Day.`,
      };
    }

    if (state === 'not_serviced') {
      if (suburb || this.#suburbOptions.length) this.#persistSuburbPicker(suburb);
      else this.#hideSuburbField();
      this.#writeStoredLocation(postcode, suburb, 'not_serviced');
      const place = suburb ? `${postcode} (${this.#titleCaseSuburb(suburb)})` : postcode;
      return {
        rows: [],
        note: `Same Day is not available to ${place} yet.`,
      };
    }

    const windows = Array.isArray(data && data.windows) ? data.windows : [];
    if (state === 'empty' || !windows.length) {
      if (suburb || this.#suburbOptions.length) this.#persistSuburbPicker(suburb);
      else this.#hideSuburbField();
      this.#writeStoredLocation(postcode, suburb, 'empty');
      const place = suburb ? `${postcode} (${this.#titleCaseSuburb(suburb)})` : postcode;
      return {
        rows: [],
        note: `Same Day is not available for ${place} right now. Cutoffs may have passed.`,
      };
    }

    // Keep suburb visible for suburb-only postcodes so shoppers can switch.
    if (suburb || this.#suburbOptions.length) this.#persistSuburbPicker(suburb);
    else this.#hideSuburbField();
    this.#writeStoredLocation(postcode, suburb, 'ok');

    const windowItems = windows.map((win) => this.#formatSameDayWindow(win)).filter(Boolean);
    const deliveryHtml = windowItems.length
      ? `<ul class="shipping-calculator__windows">${windowItems
          .map((line) => `<li class="shipping-calculator__window">${line}</li>`)
          .join('')}</ul>`
      : 'Windows shown at checkout';

    return {
      rows: [
        {
          name: 'Same Day Delivery',
          deliveryHtml,
          priceHtml: this.#sameDayPrice(),
        },
      ],
      note: '',
    };
  }

  /** Soften API copy: no em/en/hyphen dashes in customer-facing text. */
  #naturalCopy(text) {
    return String(text || '')
      .replace(/\uFFFD/g, ', ')
      .replace(/\u2014/g, ', ')
      .replace(/\u2013/g, ' to ')
      .replace(/(\d)\s*-\s*(\d)/g, '$1 to $2')
      .replace(/\s*[·•]\s*/g, ', ')
      .replace(/\s+,/g, ',')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  #formatWindowDay(iso) {
    const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    });
  }

  #formatWindowSlot(win) {
    const slug = String((win && win.window) || '')
      .toLowerCase()
      .replace(/[–—]/g, '-')
      .replace(/\s+/g, '');
    if (slug.includes('5-9')) return '5 to 9 PM';
    if (slug.includes('1-5')) return '1 to 5 PM';
    const fromLabel = String((win && win.label) || '').match(/(\d+)\s*[–\-]\s*(\d+)\s*(AM|PM)/i);
    if (fromLabel) {
      return `${fromLabel[1]} to ${fromLabel[2]} ${fromLabel[3].toUpperCase()}`;
    }
    return this.#naturalCopy((win && win.window) || '');
  }

  #formatSameDayWindowParts(win) {
    if (!win) return null;
    const day = this.#formatWindowDay(win.date);
    const slot = this.#formatWindowSlot(win);
    if (day && slot) return { day, slot };
    if (slot) return { day: 'Delivery', slot };
    const label = this.#naturalCopy(win.label || '');
    if (!label) return null;
    const split = label.match(/^(.+?),\s*(.+)$/);
    if (split) return { day: split[1], slot: split[2] };
    return { day: label, slot: '' };
  }

  #formatSameDayWindow(win) {
    const parts = this.#formatSameDayWindowParts(win);
    if (!parts) return '';
    return [parts.day, parts.slot].filter(Boolean).join(', ');
  }

  #setLoadingState(isLoading) {
    if (this.refs.submitButton) {
      this.refs.submitButton.classList.toggle('loading', isLoading);
    }
  }

  /**
   * @param {any} data
   * @param {{ rows?: Array<{name: string, deliveryHtml: string, priceHtml: string}>, note?: string }} [sameDay]
   */
  #showResults(data, sameDay = { rows: [], note: '' }) {
    if (this.refs.resultsContainer && this.refs.resultsContent) {
      this.refs.resultsContent.innerHTML = this.#formatResults(data, sameDay);
      this.refs.resultsContainer.hidden = false;
    }
  }

  /**
   * @param {any} data
   * @param {{ rows?: Array<{name: string, deliveryHtml: string, priceHtml: string}>, note?: string }} sameDay
   */
  #formatResults(data, sameDay = { rows: [], note: '' }) {
    const { options, currencyCode } = this.#extractOptions(data);
    const sameDayRows = Array.isArray(sameDay.rows) ? sameDay.rows : [];
    const note = sameDay.note || '';

    if (!options.length && !sameDayRows.length) {
      return '<p class="shipping-calculator__no-options">No shipping options available for this country.</p>';
    }

    const rows = options
      .map((raw) => this.#normalizeOption(raw, currencyCode))
      .filter((opt) => opt.name);

    const sameDayHtml = sameDayRows
      .map((row) => {
        const pendingClass = row.pending ? ' is-pending' : '';
        const delivery = String(row.deliveryHtml || '');
        const hasMarkup = delivery.includes('<');
        const detail = hasMarkup ? '' : this.#naturalCopy(delivery);
        const windows = hasMarkup ? delivery : '';
        return `
          <li class="shipping-calculator__rate shipping-calculator__rate--same-day${pendingClass}">
            <div class="shipping-calculator__rate-line">
              <div class="shipping-calculator__rate-copy">
                <span class="shipping-calculator__rate-name">${row.name}</span>
                ${detail ? `<span class="shipping-calculator__rate-detail">${detail}</span>` : ''}
                ${windows}
              </div>
              <span class="shipping-calculator__rate-price">${row.priceHtml}</span>
            </div>
          </li>`;
      })
      .join('');

    const standardHtml = rows
      .map(
        (option) => `
          <li class="shipping-calculator__rate${option.isFree ? ' shipping-calculator__rate--free' : ''}">
            <div class="shipping-calculator__rate-line">
              <div class="shipping-calculator__rate-copy">
                <span class="shipping-calculator__rate-name">${option.name}</span>
                <span class="shipping-calculator__rate-detail">${this.#formatDeliveryTime(option)}</span>
                ${
                  option.freeThreshold != null && option.freeThreshold !== ''
                    ? `<span class="shipping-calculator__condition">Free over ${this.#formatMoney(
                        option.freeThreshold,
                        option.currency
                      )}</span>`
                    : ''
                }
              </div>
              <span class="shipping-calculator__rate-price">${this.#formatPrice(option)}</span>
            </div>
          </li>`
      )
      .join('');

    return `
      <ul class="shipping-calculator__rates" aria-label="Shipping options">
        ${sameDayHtml}
        ${standardHtml}
      </ul>
      ${note ? `<p class="shipping-calculator__same-day-note">${note}</p>` : ''}
    `;
  }

  /**
   * Supports both the country-aware response ({ currencyCode, zone: { options: [...] } })
   * and legacy shapes ({ Options: [...] }, a bare array, or a single object).
   * @param {any} data
   * @returns {{ options: any[], currencyCode: string }}
   */
  #extractOptions(data) {
    let options = [];
    let currencyCode = '';

    if (data && data.zone && Array.isArray(data.zone.options)) {
      options = data.zone.options;
      currencyCode = data.currencyCode || '';
    } else if (data && Array.isArray(data.Options)) {
      options = data.Options;
    } else if (Array.isArray(data)) {
      options = data;
    } else if (data && typeof data === 'object') {
      options = [data];
    }

    if (!currencyCode) currencyCode = (data && data.currencyCode) || this.#getApiCurrency();
    return { options, currencyCode };
  }

  /**
   * Maps a raw option (new or legacy) into a common shape for rendering.
   * @param {any} o
   * @param {string} fallbackCurrency
   */
  #normalizeOption(o, fallbackCurrency) {
    const category = o.shippingCategory || '';
    const name =
      SHIPPING_CATEGORY_LABELS[category] ||
      o.Name ||
      o.name ||
      o.service_name ||
      'Standard Shipping';

    const currency = o.currencyCode || fallbackCurrency || 'USD';

    const rawPrice = o.price != null ? o.price : o.Price != null ? o.Price : o.cost != null ? o.cost : o.rate;
    const freeThreshold = o.freeThreshold != null ? o.freeThreshold : o.FreeThreshold != null ? o.FreeThreshold : null;
    const minDays = o.minBusinessDays != null ? o.minBusinessDays : o.MinDays;
    const maxDays = o.maxBusinessDays != null ? o.maxBusinessDays : o.MaxDays;
    const isFree = rawPrice === 0 || rawPrice === '0';

    return { name, currency, price: rawPrice, freeThreshold, minDays, maxDays, isFree, category };
  }

  /**
   * @param {number|string|null} amount
   * @param {string} currency
   */
  #formatMoney(amount, currency) {
    if (amount == null || amount === '') return '';
    if (typeof amount !== 'number') {
      // Legacy API may already return a formatted string (e.g. "$10").
      return String(amount);
    }
    const symbol = CURRENCY_SYMBOLS[currency] || '';
    return `${symbol}${amount} ${currency}`.trim();
  }

  #formatShippingName(option) {
    let name = option.name;
    if (option.freeThreshold != null && option.freeThreshold !== '') {
      const freeOver = this.#formatMoney(option.freeThreshold, option.currency);
      if (freeOver) {
        name += `<br><span class="shipping-calculator__condition">Free over ${freeOver}</span>`;
      }
    }
    return name;
  }

  #formatDeliveryTime(option) {
    const min = option.minDays;
    const max = option.maxDays;
    if (min && max) {
      if (min === max) return `${min} day${min > 1 ? 's' : ''}`;
      return `${min} to ${max} business days`;
    }
    if (min) return `${min}+ business days`;
    if (max) return `Up to ${max} business days`;
    return 'Standard delivery';
  }

  #formatPrice(option) {
    if (option.isFree) return 'FREE';
    const money = this.#formatMoney(option.price, option.currency);
    return money || 'FREE';
  }

  #showError(message) {
    if (this.refs.errorContainer && this.refs.errorMessage) {
      this.refs.errorMessage.textContent = message;
      this.refs.errorContainer.hidden = false;
    }
  }

  #clearError() {
    if (this.refs.errorContainer) {
      this.refs.errorContainer.hidden = true;
    }
  }
}

customElements.define('shipping-calculator-component', ShippingCalculatorComponent);
